/*
 * BEDROCK collection page behaviour. Four custom elements, one deferred file:
 *
 *   <layout-switcher>     grid / list toggle, persisted
 *   <load-more-button>    appends the next page into the existing grid
 *   <bedrock-facet-drawer>  the filter drawer below 1200px
 *   <bedrock-price-slider>  the dual-thumb price range over Dawn's number inputs
 *
 * All four are re-instantiated for free whenever facets.js swaps HTML in: it replaces
 * `#ProductGridContainer.innerHTML` and each filter group's `innerHTML`, and the parser
 * upgrades custom elements as they are inserted. State lives in the DOM or in
 * localStorage, never in a module-level variable that a re-render would desynchronise.
 *
 * Two listeners outlive their element and are the exceptions to that: the drawer's
 * `matchMedia` change handler and the MutationObserver below. Both are bound once, to
 * elements the facet re-render does not replace (the toolbar and the grid container),
 * so neither accumulates duplicates. A theme-editor section swap can strand them; that
 * costs one dead closure in the editor and nothing on the storefront.
 */

const BEDROCK_VIEW_KEY = 'bedrock:collection-view';

/* ------------------------------------------------------------------------ */
/* Grid / list switcher                                                      */
/* ------------------------------------------------------------------------ */

class LayoutSwitcher extends HTMLElement {
  connectedCallback() {
    // Rendered with `hidden` in Liquid: a scriptless client gets no dead buttons, and
    // this is the point at which they become real.
    this.hidden = false;

    this.buttons = [...this.querySelectorAll('[data-layout-mode]')];
    this.buttons.forEach((button) => button.addEventListener('click', () => this.apply(button.dataset.layoutMode)));

    this.apply(this.stored || this.dataset.default || 'grid', { persist: false });
  }

  get stored() {
    try {
      const value = localStorage.getItem(BEDROCK_VIEW_KEY);
      return value === 'grid' || value === 'list' ? value : null;
    } catch (error) {
      // Private mode / blocked storage. The switcher still works for this page view.
      return null;
    }
  }

  get grid() {
    // Re-queried on every call rather than cached: facets.js replaces the <ul> outright
    // on a filter change, so a reference captured in connectedCallback goes stale.
    return document.querySelector(this.dataset.target || '#product-grid');
  }

  apply(mode, { persist = true } = {}) {
    const grid = this.grid;
    if (!grid) return;

    grid.classList.toggle('bedrock-product-grid--list', mode === 'list');
    grid.classList.toggle('bedrock-product-grid--grid', mode !== 'list');

    this.buttons.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.layoutMode === mode));
    });

    if (!persist) return;
    try {
      localStorage.setItem(BEDROCK_VIEW_KEY, mode);
    } catch (error) {
      /* storage unavailable — the toggle is still applied above */
    }
  }
}

customElements.define('layout-switcher', LayoutSwitcher);

/* Re-apply the stored view after facets.js rebuilds the grid. Its render path does not
 * emit an event, so this hooks the one signal it does produce: the swapped-in <ul>
 * arrives without the list class, and a MutationObserver on the container catches it.
 * Cheaper than polling, and scoped to a single subtree. */
document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('ProductGridContainer');
  const switcher = document.querySelector('layout-switcher');
  if (!container || !switcher) return;

  new MutationObserver(() => {
    const mode = switcher.stored || switcher.dataset.default || 'grid';
    if (mode === 'list') switcher.apply(mode, { persist: false });
  }).observe(container, { childList: true, subtree: true });
});

/* ------------------------------------------------------------------------ */
/* Load more                                                                 */
/* ------------------------------------------------------------------------ */

class LoadMoreButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;
    this.button.addEventListener('click', this.onClick.bind(this));
  }

  async onClick() {
    const next = this.dataset.nextUrl;
    const sectionId = this.dataset.sectionId;
    if (!next || this.classList.contains('loading')) return;

    this.classList.add('loading');
    this.button.setAttribute('aria-disabled', 'true');

    try {
      // paginate.next.url already carries the active filters and sort, so the only
      // thing to add is the section rendering hint.
      const separator = next.includes('?') ? '&' : '?';
      const response = await fetch(`${next}${separator}section_id=${encodeURIComponent(sectionId)}`);
      if (!response.ok) throw new Error(`Load more failed: ${response.status}`);

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = document.getElementById('product-grid');
      const incoming = parsed.getElementById('product-grid');
      if (!grid || !incoming) throw new Error('Load more: grid missing from response');

      const items = [...incoming.children];
      items.forEach((item) => grid.appendChild(item));

      // The fetched page carries its own tail. If it has a next link we inherit it and
      // stay; if it does not, this was the last page and the button removes itself.
      const nextButton = parsed.querySelector('load-more-button');
      if (nextButton && nextButton.dataset.nextUrl) {
        this.dataset.nextUrl = nextButton.dataset.nextUrl;
        this.classList.remove('loading');
        this.button.removeAttribute('aria-disabled');
      } else {
        this.remove();
      }

      // Move focus to the first appended card. Without this, keyboard and screen-reader
      // users are returned to a button that may no longer exist, at the bottom of a page
      // that just grew by 24 products they were never told about.
      const first = items[0] && items[0].querySelector('a[href]');
      if (first) {
        first.setAttribute('tabindex', '-1');
        first.focus({ preventScroll: true });
      }
    } catch (error) {
      console.error(error);
      this.classList.remove('loading');
      this.button.removeAttribute('aria-disabled');
    }
  }
}

customElements.define('load-more-button', LoadMoreButton);

/* ------------------------------------------------------------------------ */
/* Desktop sidebar toggle                                                    */
/* ------------------------------------------------------------------------ */

class SidebarToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.sidebar = document.getElementById(this.button?.getAttribute('aria-controls'));
    if (!this.button || !this.sidebar) return;

    // Rendered `hidden` so a scriptless client never sees a button that cannot act.
    this.hidden = false;
    this.button.addEventListener('click', () => this.toggle());
  }

  /* Deliberately NOT persisted. The reference does not persist it either — measured:
   * collapse the sidebar, reload, and it is back at 274px. A filter column that stays
   * hidden across pages is a shopper wondering where the filters went, and the state is
   * cheap to re-express with one click. */
  toggle() {
    const collapsed = this.sidebar.classList.toggle('collection__sidebar--collapsed');
    this.button.setAttribute('aria-expanded', String(!collapsed));
  }
}

customElements.define('sidebar-toggle', SidebarToggle);

/* ------------------------------------------------------------------------ */
/* Filter drawer                                                             */
/* ------------------------------------------------------------------------ */

class BedrockFacetDrawer extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-facet-drawer-open]');
    this.panel = this.querySelector('.bedrock-facet-drawer__panel');
    this.overlay = this.querySelector('.bedrock-facet-drawer__overlay');
    if (!this.toggle || !this.panel) return;

    this.reparent();

    this.toggle.addEventListener('click', () => this.open());

    // At 1200px the sidebar takes over and the trigger is hidden. Widening the window
    // while the drawer is open would otherwise strand it: an open panel, no button to
    // close it, and the body still scroll-locked.
    this.desktop = window.matchMedia('(min-width: 1200px)');
    this.desktop.addEventListener('change', (event) => {
      if (event.matches) this.close();
    });

    [this.panel, this.overlay].forEach((root) => {
      if (!root) return;
      root.querySelectorAll?.('[data-facet-drawer-close]').forEach((element) =>
        element.addEventListener('click', () => this.close())
      );
      if (root.hasAttribute('data-facet-drawer-close')) root.addEventListener('click', () => this.close());
      root.addEventListener('keyup', (event) => {
        if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
      });
    });
  }

  /* The panel and overlay are moved to <body>.
   *
   * They are authored inside the toolbar because that is where the trigger lives and
   * where the markup reads sensibly, but the toolbar wrapper is `position: sticky` with
   * `z-index: 3` — which makes it a STACKING CONTEXT. A fixed-position descendant cannot
   * escape one: the panel's `z-index: 101` is resolved against its siblings inside that
   * context, not against the page, so the whole drawer sat underneath the header
   * (`z-index: 4`) and the overlay never dimmed anything. Raising the number cannot fix
   * this; only leaving the context can.
   *
   * Removing the toolbar's z-index is not the alternative it looks like: the sticky bar
   * would then paint at the same level as the product cards, which are positioned, and
   * DOM order would put the cards on top as they scroll past.
   */
  reparent() {
    // The theme editor re-renders the section without reloading, which would otherwise
    // leave the previous panel orphaned on <body> as an invisible focus trap.
    //
    // The `!== element` guard is load-bearing. Both panels carry the same id at this
    // moment — the old one appended to <body>, the new one still inside the section —
    // and `getElementById` returns the FIRST in document order, which is the new one,
    // inside <main>. Removing "the element with this id" would therefore delete the
    // panel we are about to install and leave the stale one in place: the exact
    // opposite of the intent, and invisible until a second editor edit.
    [this.panel, this.overlay].forEach((element) => {
      if (!element) return;
      if (element.id) {
        const existing = document.getElementById(element.id);
        if (existing && existing !== element) existing.remove();
      }
      document.body.appendChild(element);
    });
  }

  disconnectedCallback() {
    // Same reason, for the case where the section is removed rather than replaced.
    if (!document.body.contains(this)) {
      this.panel?.remove();
      this.overlay?.remove();
    }
  }

  get isOpen() {
    return this.panel.classList.contains('is-open');
  }

  open() {
    if (this.isOpen) return;

    this.panel.hidden = false;
    if (this.overlay) this.overlay.hidden = false;

    // The class lands on the NEXT frame. Applied in the same one, the browser has no
    // pre-transition style to interpolate from (the panel went display:none →
    // translateX(0) in a single style resolution) and it appears without sliding.
    requestAnimationFrame(() => {
      this.panel.classList.add('is-open');
      this.overlay?.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden-mobile');
      if (typeof trapFocus === 'function') trapFocus(this.panel, this.panel.querySelector('button'));
    });
  }

  close() {
    if (!this.isOpen) return;

    this.panel.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    this.toggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden-mobile');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.toggle);

    // Hide only once the slide-out has finished, so the panel is not yanked off screen.
    const finish = () => {
      if (this.isOpen) return;
      this.panel.hidden = true;
      if (this.overlay) this.overlay.hidden = true;
    };
    this.panel.addEventListener('transitionend', finish, { once: true });
    // Belt and braces: `transitionend` never fires under prefers-reduced-motion, where
    // the transition is `none`, and the panel would stay in the DOM as a focus trap.
    setTimeout(finish, 500);
  }
}

customElements.define('bedrock-facet-drawer', BedrockFacetDrawer);

/* ------------------------------------------------------------------------ */
/* Price range slider                                                        */
/* ------------------------------------------------------------------------ */

class BedrockPriceSlider extends HTMLElement {
  connectedCallback() {
    this.min = this.querySelector('.bedrock-facet__range-input--min');
    this.max = this.querySelector('.bedrock-facet__range-input--max');
    const block = this.closest('.bedrock-facet__price-block');
    const fields = block ? block.querySelectorAll('price-range input') : [];
    this.fieldMin = fields[0];
    this.fieldMax = fields[1];
    if (!this.min || !this.max || !this.fieldMin || !this.fieldMax) return;

    // `input` fires continuously while dragging and the facet form re-submits on any
    // input event it sees (debounced 800ms, but still once per pause). Stopping it here
    // keeps the drag local; the filter runs once, on release, from `change`.
    this.addEventListener('input', (event) => {
      event.stopPropagation();
      this.clamp(event.target);
      this.paint();
    });

    this.addEventListener('change', (event) => {
      event.stopPropagation();
      this.clamp(event.target);
      this.paint();
      this.commit();
    });

    // Typing in the number fields has to move the thumbs back.
    this.fieldMin.addEventListener('change', () => this.pull());
    this.fieldMax.addEventListener('change', () => this.pull());

    this.paint();
  }

  clamp(changed) {
    const min = Number(this.min.value);
    const max = Number(this.max.value);
    if (min <= max) return;
    // Push the other thumb rather than snapping the dragged one back — the shopper is
    // still holding it, and a thumb that refuses to move reads as a broken control.
    if (changed === this.min) this.max.value = String(min);
    else this.min.value = String(max);
  }

  paint() {
    const ceiling = Number(this.max.max) || 1;
    this.style.setProperty('--range-min', `${(Number(this.min.value) / ceiling) * 100}%`);
    this.style.setProperty('--range-max', `${(Number(this.max.value) / ceiling) * 100}%`);
  }

  /* Slider -> fields. The range inputs carry no `name`, so this is the only thing that
   * actually filters: writing the value and re-dispatching a bubbling `input` is what
   * reaches the facet form's listener. An empty string at either end means "no bound",
   * which keeps the URL free of a redundant 0-to-max filter.
   *
   * Two events, deliberately. Setting `.value` from script fires neither, and the two
   * listeners want different ones: the facet form submits on `input`, while Dawn's
   * <price-range> re-derives each field's `data-min`/`data-max` clamps on `change`.
   * Skipping `change` leaves those clamps describing the values from BEFORE the drag,
   * so a shopper who then types into the field is clamped against a stale bound. */
  commit() {
    const ceiling = Number(this.max.max) || 1;
    const min = Number(this.min.value);
    const max = Number(this.max.value);

    this.fieldMin.value = min > 0 ? String(min) : '';
    this.fieldMax.value = max < ceiling ? String(max) : '';

    [this.fieldMin, this.fieldMax].forEach((field) => field.dispatchEvent(new Event('change', { bubbles: true })));
    this.fieldMax.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /* Fields -> slider. */
  pull() {
    const ceiling = Number(this.max.max) || 1;
    const min = parseFloat(String(this.fieldMin.value).replace(/[^0-9.]/g, ''));
    const max = parseFloat(String(this.fieldMax.value).replace(/[^0-9.]/g, ''));

    this.min.value = String(Number.isFinite(min) ? Math.max(0, Math.min(min, ceiling)) : 0);
    this.max.value = String(Number.isFinite(max) ? Math.max(0, Math.min(max, ceiling)) : ceiling);
    this.paint();
  }
}

customElements.define('bedrock-price-slider', BedrockPriceSlider);
